import { useMemo } from "react";
import { useIntl } from "react-intl";

// Components
import { Typography, Box } from "@mui/material";
import { SquaredButton } from "pages/ProjectDashboard";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade/index";
import { CreateProjectSectionModal, useCreateProjectSectionModal } from "components/CreateProjectSectionModal";

// Hooks
import { useAbility } from "@casl/react";
import useResizeObserver from "use-resize-observer";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Ability
import { AbilityContext } from "context/Ability";

// Store
import { toggleLoader } from "store/slice/UI";
import { getCharts } from "store/slice/charts";

// Images
import createBrief from "assets/images/svgs/create-brief-white.svg";

// Types
import { BriefChildren } from "services/projects/project.types";

// Styles
import PathToImprovementStyles from "./PathToImprovement.styles";

// Others
import ldCharts from "../chartsListConfig";
import { urlPaths } from "enums/urlPaths";
import { ChartIds } from "enums/chartEnums";
import { getBriefs } from "store/slice/Project/briefs/briefsSlice";
import icons from "enums/icons";

const PathToImprovement = (): JSX.Element => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const ability = useAbility(AbilityContext);

    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");

    const { data: allChartData } = useAppSelector(getCharts);
    const { data: briefsData } = useAppSelector(getBriefs);
    const [bridgeChart] = ldCharts.filter((chart) => chart.id === ChartIds.Bridge);
    const { ref: chartWrapper, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();

    const isFreeRider = ability.can("see", "LD_FREE") || ability.can("see", "LD_LIMITED");
    const canSeeBridge = bridgeChart ? ability.can("see", bridgeChart.subject) : false;

    const classes = PathToImprovementStyles({ isFreeRider });

    const handleCallBackAfterSaving = async (createdSectionData: BriefChildren) => {
        const briefId = createdSectionData?.data.id;

        if (briefId) {
            dispatch(toggleLoader(false));
            navigate(`${urlPaths.Brief}/${companyId}?projectId=${projectId}&insightId=${insightId}&briefId=${briefId}`);
        }
    };

    const {
        showModalFor,
        newSectioName,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    } = useCreateProjectSectionModal(projectId, insightId, handleCallBackAfterSaving);

    const isBriefNameInvalid = useMemo(
        () => briefsData.some((brief) => brief.data.name === newSectioName),
        [briefsData, newSectioName]
    );

    return (
        <Box className={classes.wrapper}>
            <Box className={classes.headerContainer}>
                <Typography className={classes.mainTitle} component="h1">
                    {intl.formatMessage({
                        id: "projection.pathToImprovement",
                    })}
                </Typography>
                <Typography paragraph className={classes.intro}>
                    {intl.formatMessage({ id: "projection.pathToImprovement.description" })}
                </Typography>
                <Typography className={classes.secondaryTitle} component="h2">
                    {intl.formatMessage({
                        id: "projection.leadershipBridge",
                    })}
                </Typography>
            </Box>

            <Box>
                {!canSeeBridge && allChartData && (
                    <Box className={classes.proGraphMsgWrapper}>
                        <MessageUpgrade
                            width={330}
                            icon={icons.proStar}
                            title={"pro.message.heading"}
                            subtitle={"pro.upgrade"}
                            descriptionTitle={"pro.message.details.heading"}
                            variant="type2"
                        >
                            <Box>
                                {allChartData.graphs["LD9"]?.raw?.leadership_insight?.explainer &&
                                    allChartData.graphs["LD9"].raw.leadership_insight.explainer.map(
                                        (paragraph, index) => {
                                            return (
                                                <Box component="p" key={index}>
                                                    {paragraph}
                                                </Box>
                                            );
                                        }
                                    )}
                            </Box>
                        </MessageUpgrade>
                    </Box>
                )}
                {canSeeBridge && (
                    <>
                        <Box ref={chartWrapper} className={classes.chartWrapper}>
                            {bridgeChart?.graphComponent(width, height, {
                                createBriefCallback: handleShowCreateProjectSectionModal,
                            })}
                        </Box>
                        <Box className={classes.outroWrapper}>
                            <Box>
                                <Typography component="h3" className={classes.outroTitle}>
                                    Creating a Search Brief
                                </Typography>
                                <Typography paragraph className={classes.outroText}>
                                    {intl.formatMessage({ id: "projection.pathToImprovement.createBrief.description" })}
                                </Typography>
                            </Box>
                            <Box pl={2}>
                                <SquaredButton
                                    width={120}
                                    height={100}
                                    color="secondary"
                                    onClick={() => handleShowCreateProjectSectionModal("Brief")}
                                >
                                    <Typography sx={{ fontSize: 12, letterSpacing: 0.4, mb: 1 }}>
                                        Create Brief
                                    </Typography>
                                    <img src={createBrief} width={32} alt="" />
                                </SquaredButton>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            <CreateProjectSectionModal
                modalFor={showModalFor}
                isNameInvalid={isBriefNameInvalid}
                textInput={newSectioName}
                textInputPlaceholder=""
                handleTextInputChange={handleUpdateSectionName}
                handleCreateSection={handleCreateProjectSection}
                handleCloseModal={handleCloseProjectSectionCreateModal}
            />
        </Box>
    );
};

export default PathToImprovement;
